import { useTranslation } from 'react-i18next';
import { AlertTriangle, Link2 } from 'lucide-react';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { useMerchantAssets } from '@/hooks/use-merchant-assets';
import type { AssetType } from '@/types/merchant-asset';

/** Radix Select cannot use '' as an item value → map "no asset" to a sentinel. */
const NONE_VALUE = '__none__';

type SlotType = Extract<AssetType, 'stylesheet' | 'header' | 'background'>;

interface AssetSlotFieldProps {
  slotType: SlotType;
  merchantId: string;
  /** Current `…AssetId` value ('' = none / use inline). */
  value: string;
  onChange: (assetId: string) => void;
  id?: string;
  /** In edit mode: the service being edited, so it's excluded from the "N weiteren Diensten" count. */
  currentServiceId?: string;
}

/**
 * Slot-Picker (Spec 514 §5): pick an existing merchant asset of the slot type for a
 * service theme. The inline URL stays as the override/fallback (rendered separately).
 *
 * Orphaned reference (Entscheidung 8): if the service points to an `…AssetId` that is
 * not in the (active) asset list, the raw id is preserved as a flagged option — never
 * silently cleared. Only a deliberate pick (another asset / "none") changes it.
 */
export function AssetSlotField({ slotType, merchantId, value, onChange, id, currentServiceId }: AssetSlotFieldProps) {
  const { t } = useTranslation('merchant-assets');
  const { data: assets, isLoading } = useMerchantAssets(merchantId || undefined, slotType);

  const list = assets ?? [];
  const selected = list.find((a) => a.assetId === value);
  // Unknown while still loading OR genuinely orphaned after load.
  const unknownValue = value !== '' && !selected;
  const orphaned = unknownValue && !isLoading && assets !== undefined;

  const selectValue = value === '' ? NONE_VALUE : value;
  // „in N weiteren Diensten" — the current service is excluded (it references the asset too).
  const sharedCount = selected
    ? selected.usedBy.filter((u) => u.serviceId !== currentServiceId).length
    : 0;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{t('slot.label')}</Label>
      <Select
        value={selectValue}
        onValueChange={(v) => onChange(v === NONE_VALUE ? '' : v)}
        disabled={!merchantId || isLoading}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={t('slot.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>{t('slot.none')}</SelectItem>
          {list.map((a) => (
            <SelectItem key={a.assetId} value={a.assetId}>{a.name}</SelectItem>
          ))}
          {/* Keep an item for the current value when it's not in the list, so the
              Select can display it and the raw id survives a save (no silent clear). */}
          {unknownValue && (
            <SelectItem value={value}>{t('slot.orphaned', { id: value })}</SelectItem>
          )}
        </SelectContent>
      </Select>

      {orphaned && (
        <p className="flex items-center gap-1 text-xs text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {t('slot.orphanedHint', { id: value })}
        </p>
      )}
      {selected && sharedCount > 0 && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Link2 className="h-3 w-3 shrink-0" />
          {t('slot.shared', { count: sharedCount })}
        </p>
      )}
    </div>
  );
}
